import { useEffect, useState } from 'react';
import { Card, Loader } from './UI';
import { addDays } from '../lib/alerts';
import {
  CATS, CAT_ORDER, REASONS, currentMonday, weekLabel,
  getStaffPlanBySv, saveStaffItem, deleteStaffItem,
} from '../lib/staffplan';

const sel = { border: '1px solid #CBD5E0', background: '#fff', borderRadius: 8, padding: '8px 10px', fontSize: 13, fontWeight: 500, minWidth: 0 };
const mrcOf = (r) => {
  const m = Array.isArray(r.merchandisers) ? r.merchandisers[0] : r.merchandisers;
  return (r.is_vacancy || !m?.full_name || /вакансия/i.test(m.full_name)) ? '' : m.full_name;
};
const label = (r) => `${r.is_pm ? 'ПМ · ' : ''}${r.internal_code} · ${mrcOf(r) || 'вакансия'}`;
const OTHER = 'Другое';
const parseReason = (s) => {
  const t = String(s || '');
  if (REASONS.includes(t)) return { kind: t, text: '' };
  if (t.startsWith(OTHER + ': ')) return { kind: OTHER, text: t.slice(OTHER.length + 2) };
  return t ? { kind: OTHER, text: t } : { kind: '', text: '' };
};
const joinReason = (kind, text) => (kind === OTHER ? (text.trim() ? `${OTHER}: ${text.trim()}` : '') : kind);

// «Моё планирование · кадры»: активация / стажировка / откол по неделям
export default function StaffPlan({ routes, svIds, svId, userId, readOnly }) {
  const [week, setWeek] = useState(currentMonday());
  const [items, setItems] = useState(null);
  const [err, setErr] = useState('');
  const ids = svIds && svIds.length ? svIds : [svId];

  useEffect(() => {
    let off = false;
    setItems(null); setErr('');
    getStaffPlanBySv(ids, week).then(rows => {
      if (off) return;
      setItems(rows.map(r => {
        const pr = parseReason(r.reason);
        return { key: r.id, id: r.id, category: r.category, route_id: r.route_id, sv_id: r.sv_id, rk: pr.kind, rt: pr.text, err: '' };
      }));
    }).catch(e => { if (!off) { setErr(e.message || String(e)); setItems([]); } });
    return () => { off = true; };
    // eslint-disable-next-line
  }, [week, ids.join(',')]);

  if (!items) return <Loader />;

  const patch = (key, p) => setItems(list => list.map(i => (i.key === key ? { ...i, ...p } : i)));
  const taken = new Set(items.filter(i => i.route_id).map(i => i.route_id));

  async function persist(it, next) {
    const m = { ...it, ...next };
    const route = routes.find(r => r.id === m.route_id);
    if (!route) return;
    const reason = m.category === 'churn' ? joinReason(m.rk, m.rt) : '';
    if (m.category === 'churn' && !reason) { patch(it.key, { ...next, err: 'Укажите причину — без неё откол не сохранится' }); return; }
    try {
      if (it.id && it.route_id && it.route_id !== m.route_id) await deleteStaffItem(it.id);
      const saved = await saveStaffItem({ sv_id: route.sv_id || ids[0], route_id: m.route_id, week_start: week, category: m.category, reason, userId });
      patch(it.key, { ...next, id: saved.id, sv_id: saved.sv_id, err: '' });
    } catch (e) { patch(it.key, { ...next, err: e.message || String(e) }); }
  }

  async function remove(it) {
    try { if (it.id) await deleteStaffItem(it.id); setItems(list => list.filter(i => i.key !== it.key)); }
    catch (e) { setErr(e.message || String(e)); }
  }
  const add = (category) => setItems(list => [...list, { key: 'n' + Date.now() + Math.random(), id: null, category, route_id: '', sv_id: null, rk: '', rt: '', err: '' }]);

  const counts = Object.fromEntries(CAT_ORDER.map(c => [c, items.filter(i => i.category === c && i.id).length]));

  return (
    <Card>
      <div style={{ padding: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
          <div style={{ fontWeight: 700, fontSize: 15, flex: 1, minWidth: 160 }}>Планирование кадров</div>
          <button onClick={() => setWeek(addDays(week, -7))} style={{ border: '1px solid var(--border)', background: '#fff', borderRadius: 8, padding: '6px 12px', cursor: 'pointer' }}>‹</button>
          <span style={{ fontSize: 13, fontWeight: 700, background: '#14213D', color: '#fff', borderRadius: 8, padding: '7px 12px' }}>{weekLabel(week)}</span>
          <button onClick={() => setWeek(addDays(week, 7))} style={{ border: '1px solid var(--border)', background: '#fff', borderRadius: 8, padding: '6px 12px', cursor: 'pointer' }}>›</button>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>
          Что планирую на неделе по маршрутам. Один маршрут на неделе — в одной категории.
        </div>
        {err && <div style={{ fontSize: 12, color: 'var(--red)', marginBottom: 8 }}>{err}. Если таблицы нет — примените SQL v108.</div>}

        {CAT_ORDER.map(cat => {
          const c = CATS[cat];
          const list = items.filter(i => i.category === cat);
          return (
            <div key={cat} style={{ border: '1px solid #E2E8F0', borderRadius: 12, padding: 12, marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: c.color }} />
                <b style={{ fontSize: 14 }}>{c.label}</b>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', flex: 1 }}>{c.hint}</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: c.color }}>{counts[cat]}</span>
              </div>
              {list.map(it => {
                const vac = routes.filter(r => r.is_vacancy);
                const live = routes.filter(r => !r.is_vacancy);
                const rec = cat === 'churn' ? live : vac;
                const rest = cat === 'churn' ? vac : live;
                const ok = (r) => r.id === it.route_id || !taken.has(r.id);
                return (
                  <div key={it.key} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: 8 }}>
                    <select value={it.route_id} disabled={readOnly} style={{ ...sel, flex: '1 1 260px' }}
                      onChange={e => persist(it, { route_id: e.target.value })}>
                      <option value="">— выберите маршрут —</option>
                      <optgroup label={cat === 'churn' ? 'Действующие · рекомендуем' : 'Вакансии · рекомендуем'}>
                        {rec.filter(ok).map(r => <option key={r.id} value={r.id}>{label(r)}</option>)}
                      </optgroup>
                      <optgroup label="Остальные маршруты">
                        {rest.filter(ok).map(r => <option key={r.id} value={r.id}>{label(r)}</option>)}
                      </optgroup>
                    </select>
                    {cat === 'churn' && (
                      <div style={{ flex: '1 1 220px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <select value={it.rk} disabled={readOnly} style={{ ...sel, borderColor: !it.rk && it.route_id ? '#B3261E' : '#CBD5E0' }}
                          onChange={e => { const rk = e.target.value; patch(it.key, { rk }); if (it.route_id && (rk !== OTHER || it.rt.trim())) persist(it, { rk }); }}>
                          <option value="">Причина (обязательно)</option>
                          {REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                        </select>
                        {it.rk === OTHER && (
                          <input value={it.rt} disabled={readOnly} placeholder="Опишите причину" style={sel}
                            onChange={e => patch(it.key, { rt: e.target.value })}
                            onBlur={() => { if (it.route_id) persist(it, {}); }} />
                        )}
                      </div>
                    )}
                    {!readOnly && <button onClick={() => remove(it)} style={{ border: 'none', background: 'none', color: '#A0AEC0', fontSize: 20, cursor: 'pointer', padding: '2px 6px' }} title="Убрать">×</button>}
                    {it.err && <div style={{ width: '100%', fontSize: 12, color: '#B3261E', fontWeight: 600 }}>{it.err}</div>}
                  </div>
                );
              })}
              {!readOnly && (
                <button onClick={() => add(cat)} style={{ border: `1px dashed ${c.color}`, color: c.color, background: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>+ Добавить</button>
              )}
              {readOnly && !list.length && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Ничего не запланировано</div>}
            </div>
          );
        })}
      </div>
    </Card>
  );
}
